// The replay file (format 2): what the server saw, as a stream of full-vision world frames — not inputs to
// re-simulate. Replays are not kept across game changes: the viewer plays only a file whose
// `header.protocolVersion` and `mapHash` are this build's, and the server's store drops the rest.
//
// Frames are taken every `header.frameEvery` sim ticks (10 Hz) from tick 0 — the world as play starts,
// after the pre-match countdown — plus one last frame at the tick the match ended. Every frame is
// self-contained (no deltas), so seeking is a binary search on `world.tick` (frameIndexAt) and nothing else.
//
// How a viewer consumes a frame:
//   - `frameAsSnapshot(frame, view, seat)` turns it into the SnapshotMessage a live client would have got:
//     view null = everything ("all"), 'a' / 'b' = exactly that team's fogged snapshot (heroes hidden as
//     `{ id, visible: false }`, lob endpoints nulled, hop origins substituted, events filtered — the same
//     rules server/snapshot.ts applies live). `seat` picks whose private `you` (cooldowns, effects) rides
//     along, for a HUD following that hero; its `ink` is the viewed team's pool (a team never sees the
//     rival's). Feed the result to a RemoteGameView-like renderer.
//   - `world.events` are ALL the fx/num/anim events since the previous frame (so up to 100 ms late); fire
//     them once, when playback crosses the frame, and not again when seeking backwards onto it.
//   - Between frames, interpolate positions exactly as the live client does between 20 Hz snapshots.
//   - `kda[heroId]` is the running kills/deaths/assists; `header.stats` the final full table.
//   - Names (`header.roster[].name`) are player-chosen: escape on render.
import { HEROES, type HeroKind } from '../../core/heroes.js';
import type { Team } from '../../core/layout.js';
import type { MatchStats } from '../../core/stats.js';
import { parseMatchStats } from '../matchStats.js';
import type { SeatIndex, SnapshotMessage } from '../protocol.js';

export const REPLAY_FORMAT = 2;
/** A frame every this many sim ticks: 60 Hz / 6 = 10 Hz. */
export const REPLAY_FRAME_EVERY = 6;

/** The shared world half of a snapshot, built with full vision (server/snapshot.ts buildFullSnapshotCommon). */
export type ReplayWorld = Omit<SnapshotMessage, 'ackSeq' | 'you' | 'countdown'>;
/** One hero's private snapshot half (`SnapshotMessage.you`), per hero id. */
export type ReplaySeat = SnapshotMessage['you'];

/** Per entry of the matching `world` list, which team's fog lets it through: bit 1 = team a, bit 2 = team b.
    `heroes` add 4 / 8: that team saw the current hop start (otherwise its origin is hidden). `lobs` use
    1 / 2 for `from` and 4 / 8 for `to` (a lob is visible when either endpoint is). `events` add 4 / 8: that team
    is not told who dealt a hit (its attacker was out of sight), so its `by` is dropped. */
export type ReplayVisibility = {
  heroes: number[]; monsters: number[]; summons: number[]; projectiles: number[]; lobs: number[]; pools: number[];
  traps: number[]; barriers: number[]; reveals: number[]; totems: number[]; events: number[];
};
const VIS_LISTS = ['heroes', 'monsters', 'summons', 'projectiles', 'lobs', 'pools', 'traps', 'barriers', 'reveals', 'totems', 'events'] as const;

export type ReplayFrame = {
  world: ReplayWorld;
  seats: ReplaySeat[];
  kda: [kills: number, deaths: number, assists: number][];
  vis: ReplayVisibility;
};

export type ReplayRosterEntry = { kind: HeroKind; team: Team; /** null = a bot. Hostile input — escape on render. */ name: string | null };

export type ReplayHeader = {
  id: string;
  /** The protocol the frames' snapshot shapes follow. */
  protocolVersion: number;
  mapHash: string;
  seed: number;
  /** UTC, ISO 8601: when the match was set up (its countdown included). */
  startedAt: string;
  /** Seconds per sim tick. */
  dt: number;
  frameEvery: number;
  /** In hero id (= seat) order. */
  roster: ReplayRosterEntry[];
  /** The last recorded tick: the match's end, or the cap when `truncated`. */
  endedTick: number;
  /** Match clock at the end, seconds. */
  durationSec: number;
  /** The recording hit its size cap (a marathon overtime): frames stop at `endedTick`, while winner, score
      and stats still describe the real end. */
  truncated: boolean;
  /** The match did not finish on its own: every human left, or the simulation threw. */
  aborted: boolean;
  winner: Team | null;
  score: { a: number; b: number };
  /** Null when the end-of-match stats could not be taken (a crash) or did not parse. */
  stats: MatchStats | null;
};

/** On disk the header is serialized after the frames (it is only known at the end, and the frames are
    compressed as they come); `format` comes first so a file can be sniffed cheaply. */
export type ReplayFile = { format: typeof REPLAY_FORMAT; frames: ReplayFrame[]; header: ReplayHeader };

/** What the replay list shows — nothing a viewer couldn't see on the results screen. */
export type ReplaySummary = {
  id: string;
  startedAt: string;
  durationSec: number;
  heroes: ReplayRosterEntry[];
  score: { a: number; b: number };
  winner: Team | null;
  aborted: boolean;
  truncated: boolean;
};

export function summarizeReplay(h: ReplayHeader): ReplaySummary {
  return {
    id: h.id, startedAt: h.startedAt, durationSec: Math.round(h.durationSec), heroes: h.roster.map(r => ({ ...r })),
    score: { ...h.score }, winner: h.winner, aborted: h.aborted, truncated: h.truncated,
  };
}

/** 128 random bits, base64url without padding — the id alone grants access, so it must stay unguessable. */
const REPLAY_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;
export function isValidReplayId(v: unknown): v is string {
  return typeof v === 'string' && REPLAY_ID_PATTERN.test(v);
}

/** Index of the last frame at or before `tick` (0 when `tick` precedes them all); -1 for no frames. */
export function frameIndexAt(frames: readonly ReplayFrame[], tick: number): number {
  let lo = 0, hi = frames.length - 1, found = frames.length ? 0 : -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid]!.world.tick <= tick) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found;
}

/** The snapshot `view`'s team would have received live at this frame (null = full vision), carrying hero
    `seat`'s private half as `you`. See the header comment. */
export function frameAsSnapshot(frame: ReplayFrame, view: Team | null, seat: SeatIndex): SnapshotMessage {
  const { world, vis } = frame;
  let you = frame.seats[seat] ?? frame.seats[0]!;
  if (view === null) return { ...world, ackSeq: 0, you };
  const bit = view === 'a' ? 1 : 2;
  // `ink` is the seat's TEAM pool: a team's view shows its own pool whichever hero is followed, never the rival's.
  // Same for `relicCd`/`eyeFor`.
  const teamOf = (i: number) => { const h = world.heroes[i]; return h?.visible ? h.team : null; };
  if (teamOf(seat) !== view) {
    const own = frame.seats.find((_, i) => teamOf(i) === view);
    // The relic's cooldown and an open Око are the team's too.
    you = { ...you, ink: own ? own.ink : 0, relicCd: own?.relicCd ?? 0, eyeFor: own?.eyeFor ?? 0 };
  }
  const keep = <T>(list: T[], bits: number[]) => list.filter((_, i) => ((bits[i] ?? 0) & bit) !== 0);
  // `confused`/`sleepless` come only from an enemy Кошмар (one per match, no duplicate heroes): the live
  // snapshot sends them only while the team sees him, so the same here.
  const nightmareSeen = (team: Team) => world.heroes.some((n, i) => n.visible && n.hero === 'nightmare' && n.team !== team && ((vis.heroes[i] ?? 0) & bit) !== 0);
  return {
    ...world, ackSeq: 0, you,
    heroes: world.heroes.map((h, i) => {
      const v = vis.heroes[i] ?? 0;
      if (!h.visible || !(v & bit)) return { id: h.id, visible: false };
      if (h.tether) {  // Связка's far end: only where this team's fog let it through (snapshot.ts tetherView)
        const { seen = 0, to } = h.tether;
        h = { ...h, tether: to && (seen & bit) ? { to } : {} };
      }
      if ((h.confused || h.sleepless) && !nightmareSeen(h.team)) {
        const { confused: _c, sleepless: _s, ...rest } = h;
        h = rest;
      }
      if (!h.hop || v & (bit << 2)) return h;
      // Came into sight mid-leap: the live snapshot restarts the hop from where the hero is now.
      return { ...h, hop: { ...h.hop, from: { ...h.pos }, dur: Math.max(0, h.hop.dur - h.stateT) } };
    }),
    monsters: world.monsters.map((m, i) => (m.visible && ((vis.monsters[i] ?? 0) & bit) ? m : { id: m.id, kind: m.kind, visible: false })),
    summons: keep(world.summons, vis.summons),
    projectiles: keep(world.projectiles, vis.projectiles),
    lobs: world.lobs.flatMap((l, i) => {
      const v = vis.lobs[i] ?? 0, from = (v & bit) !== 0, to = (v & (bit << 2)) !== 0;
      return from || to ? [{ ...l, from: from ? l.from : null, to: to ? l.to : null }] : [];
    }),
    pools: keep(world.pools, vis.pools),
    traps: keep(world.traps, vis.traps),
    barriers: keep(world.barriers, vis.barriers),
    reveals: keep(world.reveals, vis.reveals),
    totems: keep(world.totems, vis.totems),
    events: world.events.flatMap((e, i) => {
      const v = vis.events[i] ?? 0;
      if (!(v & bit)) return [];
      if (!(v & (bit << 2))) return [e];
      const { by: _, ...told } = e as Extract<typeof e, { name: 'hit' }>;
      return [told];
    }),
  };
}

// ---- Tolerant parsing -------------------------------------------------------------------------------------
// A replay is downloaded by anyone holding a link, so the client reads it defensively: nothing here throws,
// a malformed frame is dropped (the rest still plays), and a file whose header or format is unusable is null.

const NAME_MAX = 24;
const MAX_HEROES = 8;
type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0;
const isTeam = (v: unknown): v is Team => v === 'a' || v === 'b';
const isVec = (v: unknown) => isObj(v) && isNum(v.x) && isNum(v.z);
const isScore = (v: unknown) => isObj(v) && isNum(v.a) && isNum(v.b);
const isArrOf = (v: unknown, ok: (e: unknown) => boolean) => Array.isArray(v) && v.every(ok);
const hasId = (e: unknown): e is Obj => isObj(e) && isNum(e.id);

function isHeroSnapshot(e: unknown): boolean {
  if (!hasId(e) || typeof e.visible !== 'boolean') return false;
  if (!e.visible) return true;
  return typeof e.hero === 'string' && Object.hasOwn(HEROES, e.hero) && isTeam(e.team) && typeof e.alive === 'boolean'
    && isVec(e.pos) && isNum(e.yaw) && isNum(e.hp) && isNum(e.maxHp) && typeof e.state === 'string' && isNum(e.stateT)
    && (e.hop === undefined || (isObj(e.hop) && isVec(e.hop.from) && isVec(e.hop.to) && isNum(e.hop.dur)));
}
function isMonsterSnapshot(e: unknown): boolean {
  if (!hasId(e) || typeof e.kind !== 'string' || typeof e.visible !== 'boolean') return false;
  return !e.visible || (typeof e.alive === 'boolean' && isVec(e.pos) && isNum(e.yaw) && isNum(e.hp) && isNum(e.maxHp));
}
const isPlaced = (e: unknown) => hasId(e) && isVec(e.pos);
const isSummon = (e: unknown) => isPlaced(e) && isTeam((e as Obj).team) && typeof (e as Obj).kind === 'string' && isNum((e as Obj).hp) && isNum((e as Obj).maxHp);
const isLob = (e: unknown) => hasId(e) && (e.from === null || isVec(e.from)) && (e.to === null || isVec(e.to)) && isNum(e.t0) && isNum(e.flight);
const isBarrier = (e: unknown) => hasId(e) && isVec(e.center) && isVec(e.dir) && isNum(e.half);
const isReveal = (e: unknown) => hasId(e) && isNum(e.x) && isNum(e.z) && isNum(e.r);
const isSeat = (e: unknown) => isObj(e) && isNum(e.cdQ) && isNum(e.cdW) && isNum(e.ink) && isObj(e.artifactCdFor) && isObj(e.effects)
  && Array.isArray(e.artifactLocks);

function isWorld(w: unknown): w is ReplayWorld {
  return isObj(w) && isInt(w.tick) && isNum(w.dt) && isNum(w.clock) && isScore(w.score) && typeof w.overtime === 'boolean'
    && (w.winner === null || isTeam(w.winner)) && isObj(w.teamBuffs)
    && isArrOf(w.points, p => isObj(p) && typeof p.id === 'string' && isNum(p.progress))
    && isArrOf(w.heroes, isHeroSnapshot) && isArrOf(w.monsters, isMonsterSnapshot) && isArrOf(w.summons, isSummon)
    && isArrOf(w.projectiles, isPlaced) && isArrOf(w.lobs, isLob) && isArrOf(w.pools, isPlaced) && isArrOf(w.traps, isPlaced)
    && isArrOf(w.barriers, isBarrier) && isArrOf(w.reveals, isReveal) && isArrOf(w.totems, isPlaced)
    && isArrOf(w.events, e => isObj(e) && typeof e.type === 'string');
}

function parseFrame(raw: unknown, heroCount: number): ReplayFrame | null {
  if (!isObj(raw) || !isWorld(raw.world) || !isObj(raw.vis)) return null;
  const world = raw.world as ReplayWorld & Obj, vis = raw.vis;
  if (world.heroes.length !== heroCount) return null;
  for (const k of VIS_LISTS) {
    const bits = vis[k], list = world[k];
    if (!Array.isArray(bits) || !Array.isArray(list) || bits.length !== list.length || !bits.every(b => isInt(b) && b < 16)) return null;
  }
  if (!isArrOf(raw.seats, isSeat) || (raw.seats as unknown[]).length !== heroCount) return null;
  if (!isArrOf(raw.kda, k => Array.isArray(k) && k.length === 3 && k.every(isInt)) || (raw.kda as unknown[]).length !== heroCount) return null;
  return raw as ReplayFrame;
}

function parseHeader(raw: unknown): ReplayHeader | null {
  if (!isObj(raw) || !isValidReplayId(raw.id)) return null;
  if (!isInt(raw.protocolVersion) || typeof raw.mapHash !== 'string' || !isNum(raw.seed) || typeof raw.startedAt !== 'string') return null;
  if (!isNum(raw.dt) || raw.dt <= 0 || !isInt(raw.frameEvery) || raw.frameEvery < 1 || !isInt(raw.endedTick) || !isNum(raw.durationSec)) return null;
  if (!isScore(raw.score) || !(raw.winner === null || isTeam(raw.winner))) return null;
  if (!Array.isArray(raw.roster) || raw.roster.length < 1 || raw.roster.length > MAX_HEROES) return null;
  const roster: ReplayRosterEntry[] = [];
  for (const r of raw.roster) {
    if (!isObj(r) || typeof r.kind !== 'string' || !Object.hasOwn(HEROES, r.kind) || !isTeam(r.team)) return null;
    roster.push({ kind: r.kind as HeroKind, team: r.team, name: typeof r.name === 'string' ? r.name.slice(0, NAME_MAX) : null });
  }
  const score = raw.score as { a: number; b: number };
  return {
    id: raw.id, protocolVersion: raw.protocolVersion, mapHash: raw.mapHash, seed: raw.seed, startedAt: raw.startedAt,
    dt: raw.dt, frameEvery: raw.frameEvery, roster, endedTick: raw.endedTick, durationSec: raw.durationSec,
    truncated: raw.truncated === true, aborted: raw.aborted === true, winner: raw.winner,
    score: { a: score.a, b: score.b }, stats: parseMatchStats(raw.stats),
  };
}

/** A downloaded replay (already JSON-parsed), or null when it is not a usable format-2 file. Frames that are
    malformed or out of tick order are dropped; a file left with no frames at all is null too. */
export function parseReplayFile(raw: unknown): ReplayFile | null {
  if (!isObj(raw) || raw.format !== REPLAY_FORMAT || !Array.isArray(raw.frames)) return null;
  const header = parseHeader(raw.header);
  if (!header) return null;
  const frames: ReplayFrame[] = [];
  for (const f of raw.frames) {
    const frame = parseFrame(f, header.roster.length);
    if (frame && (!frames.length || frame.world.tick > frames[frames.length - 1]!.world.tick)) frames.push(frame);
  }
  return frames.length ? { format: REPLAY_FORMAT, frames, header } : null;
}
